import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  Box,
  Stack,
} from "@mui/material";
import { Close as CloseIcon, PhotoCamera } from "@mui/icons-material";
import { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axios";

export const MARKING_TYPE_LABELS: Record<string, string> = {
  VAURIO: "Vaurio",
  ESTE: "Este",
  KORJAUSEHDOTUS: "Korjausehdotus",
  MUU: "Muu havainto",
};

export const MARKING_STATUS_LABELS: Record<string, string> = {
  OPEN: "Avoin",
  ACKNOWLEDGED: "Kuitattu",
  RESOLVED: "Hoidettu",
};

export const MARKING_STATUS_COLORS: Record<string, string> = {
  OPEN: "#d32f2f",
  ACKNOWLEDGED: "#ed6c02",
  RESOLVED: "#2e7d32",
};

export type PointOfInterestData = {
  id?: number;
  type: string;
  description: string;
  lat: number;
  lng: number;
  status?: string;
  reporterName?: string;
  createdAt?: string;
  photoFilename?: string | null;
};

interface PointModalProps {
  open: boolean;
  onClose: () => void;
  location: { lat: number; lng: number };
  mode: "view" | "edit" | "create";
  data?: PointOfInterestData;
  onSubmit: (data: PointOfInterestData & { photoFile?: File | null }) => void;
  onDelete?: () => void;
  /** true when the current user may change the marking's status (admin) */
  canManage?: boolean;
  onStatusChange?: (status: string) => void;
}

/** Fetches the photo through the authenticated axios client (a plain <img>
 *  src can't carry the Bearer token) and shows it as an object URL. */
const MarkingPhoto = ({ filename }: { filename: string }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    axiosInstance
      .get(`/files/preview/${filename}`, { responseType: "blob" })
      .then((res) => {
        objectUrl = URL.createObjectURL(res.data);
        if (!cancelled) setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [filename]);

  if (failed) return null;
  if (!url) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  return (
    <Box
      component="img"
      src={url}
      alt="Merkinnän kuva"
      sx={{ maxWidth: "100%", maxHeight: 240, borderRadius: 1 }}
    />
  );
};

const PointModal: React.FC<PointModalProps> = ({
  open,
  onClose,
  location,
  mode,
  data,
  onSubmit,
  onDelete,
  canManage = false,
  onStatusChange,
}) => {
  const isCreate = mode === "create";
  const [type, setType] = useState(data?.type || "VAURIO");
  const [description, setDescription] = useState(data?.description || "");
  const [photoFile, setPhotoFile] = useState<File | null>(null);

  useEffect(() => {
    if (open) {
      setType(data?.type || "VAURIO");
      setDescription(data?.description || "");
      setPhotoFile(null);
    }
  }, [open, data]);

  const handleSave = () => {
    onSubmit({
      type,
      description,
      lat: location.lat,
      lng: location.lng,
      photoFile,
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 3,
          padding: 1,
          minWidth: 400,
        },
      }}
    >
      <DialogTitle>
        {isCreate ? "Lisää merkintä kartalle" : "Merkinnän tiedot"}
        {!isCreate && data?.status && (
          <Chip
            label={MARKING_STATUS_LABELS[data.status] ?? data.status}
            size="small"
            sx={{
              ml: 2,
              color: "white",
              backgroundColor: MARKING_STATUS_COLORS[data.status] ?? "grey",
            }}
          />
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Tyyppi"
            select
            fullWidth
            value={type}
            onChange={(e) => setType(e.target.value)}
            disabled={!isCreate}
          >
            {Object.entries(MARKING_TYPE_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Kuvaus"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={!isCreate}
            placeholder="Esim. syvä kuoppa tien oikeassa reunassa"
          />
          {isCreate && (
            <Box>
              <Button
                component="label"
                variant="outlined"
                size="small"
                startIcon={<PhotoCamera />}
              >
                {photoFile ? "Vaihda kuva" : "Lisää kuva"}
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPhotoFile(e.target.files?.[0] ?? null)}
                />
              </Button>
              {photoFile && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="span"
                  sx={{ ml: 1 }}
                >
                  {photoFile.name}
                  <IconButton
                    size="small"
                    aria-label="Poista kuva"
                    onClick={() => setPhotoFile(null)}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                </Typography>
              )}
            </Box>
          )}
          {!isCreate && data?.photoFilename && (
            <MarkingPhoto filename={data.photoFilename} />
          )}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Sijainti
            </Typography>
            <Typography variant="body2">
              Lat: {location.lat.toFixed(5)}
              <br />
              Lng: {location.lng.toFixed(5)}
            </Typography>
            {!isCreate && (data?.reporterName || data?.createdAt) && (
              <Typography variant="body2" color="text.secondary" mt={1}>
                {data?.reporterName ?? ""}
                {data?.createdAt
                  ? ` · ${new Date(data.createdAt).toLocaleDateString("fi-FI")}`
                  : ""}
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        {isCreate ? (
          <>
            <Button onClick={onClose} color="inherit">
              Peruuta
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={!description.trim()}
            >
              Lisää merkintä
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} color="inherit">
              Sulje
            </Button>
            {canManage && onStatusChange && data?.status === "OPEN" && (
              <Button
                onClick={() => {
                  onStatusChange("ACKNOWLEDGED");
                  onClose();
                }}
                variant="outlined"
                color="warning"
              >
                Kuittaa
              </Button>
            )}
            {canManage && onStatusChange && data?.status !== "RESOLVED" && (
              <Button
                onClick={() => {
                  onStatusChange("RESOLVED");
                  onClose();
                }}
                variant="contained"
                color="success"
              >
                Merkitse hoidetuksi
              </Button>
            )}
            {canManage && onStatusChange && data?.status === "RESOLVED" && (
              <Button
                onClick={() => {
                  onStatusChange("OPEN");
                  onClose();
                }}
                variant="outlined"
                color="warning"
              >
                Avaa uudelleen
              </Button>
            )}
            {onDelete && (
              <Button
                onClick={() => {
                  onDelete();
                  onClose();
                }}
                color="error"
              >
                Poista
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PointModal;
